import { Button, Checkbox, TextField, Typography } from '@mui/material';
import { useEnter, useEscape, useKeyPresses } from '@qfa/react-components';
import Dialog from 'components/Dialog';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import usePresetDialogStyles from './styles';
import { PresetDialogProps } from './types';

const PresetDialog: React.FunctionComponent<PresetDialogProps> = (props) => {
  const {
    preset,
    savePreset,
    open,
    handleClosePresetDialog,
    hasConfigurePrivilege,
    hasUpdatePrivilege,
    hasCreatePrivilege,
    cameraName,
  } = props;
  const classes = usePresetDialogStyles();
  const { t } = useTranslation(['video', 'common']);

  const [presetName, setPresetName] = useState<string | null>(preset?.name ?? null);
  const [isSystemPreset, setIsSystemPreset] = useState<boolean>(preset?.isSystem ?? false);

  const handleClickOutsideDialog = useCallback(
    (event: MouseEvent) => {
      open && event.stopImmediatePropagation();
    },
    [open]
  );

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutsideDialog, {
      capture: true,
    });
    return () => {
      window.removeEventListener('mousedown', handleClickOutsideDialog, {
        capture: true,
      });
    };
  }, [handleClickOutsideDialog, open]);

  const handleSavePreset = useCallback(() => {
    if (!presetName) {
      return;
    }
    savePreset?.(presetName, isSystemPreset, preset && preset.id);
    setPresetName('');
    setIsSystemPreset(false);
    handleClosePresetDialog();
  }, [handleClosePresetDialog, isSystemPreset, preset, presetName, savePreset]);

  const cancel = () => {
    setPresetName('');
    setIsSystemPreset(false);
    handleClosePresetDialog();
  };

  const onKeyUp = useKeyPresses(
    useEnter({
      callback: () => {
        !isSaveButtonDisabled && handleSavePreset();
      },
    }),
    useEscape({ callback: cancel })
  );

  const hasPrivilege = useMemo(() => {
    if (isSystemPreset) {
      return hasConfigurePrivilege;
    }
    if (preset) {
      return hasUpdatePrivilege;
    }

    return hasCreatePrivilege;
  }, [hasConfigurePrivilege, hasCreatePrivilege, hasUpdatePrivilege, isSystemPreset, preset]);

  const isSaveButtonDisabled = useMemo(() => {
    if (!presetName) {
      return true;
    }
    return !hasPrivilege;
  }, [hasPrivilege, presetName]);

  const renderPresetDialog = () => {
    return (
      <Dialog
        open={open}
        title={preset ? t('video:editPreset') : `${t('common:savecurrent')} ${t('video:asPreset')}`}
        dialogProps={{ onKeyUp }}
        buttons={
          <>
            <Button onClick={cancel}>{t('common:cancel')}</Button>
            <Button
              disabled={isSaveButtonDisabled}
              onClick={() => handleSavePreset()}
              variant='contained'
              color='warning'
            >
              {t('common:save')}
            </Button>
          </>
        }
      >
        {!preset && (
          <Typography variant='subtitle2'>
            {`${t('video:saveCurrentPosition')} ${t('video:forCamera')} ${cameraName} ${t(
              'video:asAPreset'
            )}`}
          </Typography>
        )}
        <TextField
          variant='outlined'
          className={classes.presetFieldDialog}
          onBlur={(e) => setPresetName(e.currentTarget.value)}
          onChange={(e) => setPresetName(e.currentTarget.value)}
          onClick={(e) => e.stopPropagation()}
          required
          placeholder={t('video:presetName')}
          size='small'
          fullWidth
          disabled={!hasPrivilege}
          value={presetName}
        />
        <div className={clsx(classes.presetFieldDialog, classes.systemPresetWrapper)}>
          <Checkbox
            checked={isSystemPreset}
            onChange={(event) => {
              event.stopPropagation();
              setIsSystemPreset(event.target.checked);
            }}
            disabled={!hasConfigurePrivilege}
          />
          <Typography variant='body1'>{t('video:systemPreset')}</Typography>
        </div>
      </Dialog>
    );
  };

  return renderPresetDialog();
};

export default PresetDialog;
